// Forces which CMS (Sanity or Sitecore) a page is resolved from, see `server/getServerSideProps.ts`. Sitecore
// stays the implicit default; this only re-attaches an override that was already active on the current page, so
// it survives client-side/container-hop navigation via `Link`, and a redirect through `keepCmsOverride`, instead
// of silently falling back to Sitecore.
//
// TODO: Sanity-migration-only escape hatch — delete this file and its usage in `Link.tsx`, `useRedirect` and the
// flows' `FlowRouter` once every container has migrated to Sanity, alongside `_cms`/`allowCmsOverrideInProduction`
// in `server/getServerSideProps.ts`.
export const CMS_QUERY_PARAM = '_cms';

export const isCmsOverride = (value: unknown): value is 'sanity' | 'sitecore' => value === 'sanity' || value === 'sitecore';

export const withCmsParam = (url: string, cms: string): string => {
  const [pathAndQuery, hash] = url.split('#');
  const queryStart = pathAndQuery.indexOf('?');
  // The parsed query, not the raw text: `?previous_cms=` or `#_cms=` names no override.
  if (queryStart !== -1 && new URLSearchParams(pathAndQuery.slice(queryStart)).has(CMS_QUERY_PARAM)) return url;
  const separator = queryStart !== -1 ? '&' : '?';
  const withParam = `${pathAndQuery}${separator}${CMS_QUERY_PARAM}=${cms}`;
  return hash !== undefined ? `${withParam}#${hash}` : withParam;
};

/**
 * The override a query string asks for, as one of the two values this code writes itself. Matching it and returning
 * a literal, rather than the text that was read, keeps anything from the visitor's address out of the address it
 * goes into.
 */
const overrideIn = (search: string): 'sanity' | 'sitecore' | undefined => {
  switch (new URLSearchParams(search).get(CMS_QUERY_PARAM)) {
    case 'sanity':
      return 'sanity';
    case 'sitecore':
      return 'sitecore';
    default:
      return undefined;
  }
};

/**
 * `url` with the override of the address the visitor is on, for an address code builds and navigates to itself,
 * such as a redirect after a form. `Link` does the same for the links it renders.
 *
 * Only a valid override goes along, and only to an address on this site: relative, or on the same origin. That is
 * decided by the address rather than by the link's type, because code marks a link to another container of this
 * site as external just to force a full page load. A page elsewhere cannot be assumed to honour `_cms`. Anything
 * else comes back unchanged, and so does every address on the server, where there is no current address to read.
 */
export const keepCmsOverride = (url: string): string => {
  if (typeof window === 'undefined') return url;
  const cms = overrideIn(window.location.search);
  if (!cms) return url;
  const { origin } = window.location;
  try {
    return new URL(url, origin).origin === origin ? withCmsParam(url, cms) : url;
  } catch {
    return url;
  }
};
